import { useEffect, useState } from 'react';
import {
  invitationsEmployeur,
  type MonEmployeur,
  type RoleChezEmployeur,
} from '@/services/invitationsEmployeur.service';
import { organisationsService } from '@/services/organisations.service';
import { sirhService } from '@/features/sirh/services/sirh.service';
import type { SirhMe } from '@/features/sirh/types';

/**
 * Cette personne appartient-elle À UNE ENTREPRISE CLIENTE, et à rien d'autre ?
 *
 * POURQUOI CETTE QUESTION EXISTE. Constaté sur capture le 25/08/2026 : la
 * direction d'une entreprise cliente, en se connectant, atterrissait sur
 * « Tous les dossiers » — l'écran de travail du cabinet — avec le menu complet
 * du cabinet : Domaines, Automatisations, GED, Templates, Expéditeurs,
 * **Facturation**, Organisation. Elle n'y voyait aucune donnée (le cloisonnement
 * de C1 fait son travail) mais elle voyait l'INTÉRIEUR du logiciel de son
 * cabinet de paie, et une entrée « Facturation » qui n'est pas la sienne.
 *
 * Le cloisonnement des données était fait ; celui de l'interface ne l'était pas.
 *
 * COMMENT ON TRANCHE. Deux questions au serveur, et la réponse est le
 * croisement : elle tient un rôle dans au moins une entreprise, ET elle n'est
 * membre d'aucun cabinet. Quelqu'un qui est les deux — un gestionnaire qui est
 * aussi dirigeant de sa propre société — reste traité en cabinet : on ne lui
 * retire rien.
 *
 * `null` tant qu'on ne sait pas : afficher un menu puis le changer sous les
 * yeux de l'utilisateur est pire que d'attendre un instant.
 */
export interface EtatDuClient {
  /** `null` tant qu'on ne sait pas. */
  estClientSeul: boolean | null;
  /** Ce que son cabinet lui a ouvert. Vide tant qu'on ne sait pas. */
  fonctionnalitesSirh: string[];
  bdeseOuverte: boolean;
  /** Le dossier à ouvrir pour la base économique et sociale. */
  folderUuid: string | null;
  /** Sert à proposer « Mon entreprise » aux profils mixtes cabinet/client. */
  aDesEntreprises: boolean;
  /** Aucun rôle n'est déduit quand les deux sources d'autorisation ne répondent pas. */
  erreurChargement: boolean;
  /** Capacités issues de l'acteur SIRH réel, jamais du seul rôle employeur. */
  peutVoirAbsences: boolean;
  peutVoirApprobations: boolean;
}

type OrganisationConnue = unknown;

const ROLES_BDESE = new Set<RoleChezEmployeur>(['admin', 'drh', 'cse']);

function rolesDe(entreprise: MonEmployeur): RoleChezEmployeur[] {
  return entreprise.roles?.length ? entreprise.roles : [entreprise.role];
}

/**
 * Résume les autorisations sans confondre le premier dossier avec le premier
 * dossier dont la BDESE est réellement ouverte.
 */
export function construireEtatDuClient(
  entreprises: MonEmployeur[],
  cabinets: OrganisationConnue[],
  acteurSirh: SirhMe
): EtatDuClient {
  const sirh = new Set<string>();
  let dossierBdese: string | null = null;
  let peutAdministrerEntreprise = false;

  for (const entreprise of entreprises) {
    const roles = rolesDe(entreprise);
    peutAdministrerEntreprise ||= roles.includes('admin');
    if (entreprise.modules?.sirh?.actif) {
      for (const fonctionnalite of entreprise.modules.sirh.fonctionnalites ??
        []) {
        sirh.add(fonctionnalite);
      }
    }
    if (
      entreprise.modules?.bdese?.actif &&
      roles.some(role => ROLES_BDESE.has(role)) &&
      !dossierBdese
    ) {
      dossierBdese = entreprise.folderUuid ?? null;
    }
  }

  const aUnEmploi = acteurSirh.ownEmploymentUuids.length > 0;
  const peutVoirAbsences = acteurSirh.capabilities?.viewOwnLeave ?? aUnEmploi;
  const peutVoirApprobations =
    acteurSirh.capabilities?.reviewLeaveRequests ??
    acteurSirh.managedEmploymentUuids.length > 0;

  return {
    estClientSeul:
      cabinets.length === 0 && (entreprises.length > 0 || aUnEmploi),
    fonctionnalitesSirh: [...sirh],
    bdeseOuverte: dossierBdese !== null,
    folderUuid: dossierBdese,
    // Cette entrée ouvre la gestion des accès. Une relation salariée, un
    // mandat CSE ou un périmètre manager ne suffisent donc jamais à l'afficher.
    aDesEntreprises: peutAdministrerEntreprise,
    erreurChargement: false,
    peutVoirAbsences,
    peutVoirApprobations,
  };
}

const ETAT_INITIAL: EtatDuClient = {
  estClientSeul: null,
  fonctionnalitesSirh: [],
  bdeseOuverte: false,
  folderUuid: null,
  aDesEntreprises: false,
  erreurChargement: false,
  peutVoirAbsences: false,
  peutVoirApprobations: false,
};

/** La version détaillée : qui elle est, ET ce qui lui est ouvert. */
export function useEtatDuClient(): EtatDuClient {
  const [etat, setEtat] = useState<EtatDuClient>(ETAT_INITIAL);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const [entreprises, cabinets, acteurSirh] = await Promise.all([
          invitationsEmployeur.miens(),
          organisationsService.getOrganisations(),
          sirhService.me(),
        ]);
        if (!vivant) return;
        setEtat(construireEtatDuClient(entreprises, cabinets, acteurSirh));
      } catch {
        if (!vivant) return;
        // Ne jamais transformer une panne d'autorisation en profil cabinet :
        // cela ferait réapparaître les écrans réservés pendant l'incident.
        setEtat({ ...ETAT_INITIAL, erreurChargement: true });
      }
    })();
    return () => {
      vivant = false;
    };
  }, []);

  return etat;
}
